import { Box, Typography, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { useWhitelabel } from "../../../context/WhitelabelContext";

// Two-column shell shared by every guided onboarding screen: a scrollable content
// column on the left and a hero image with contextual copy on the right. Mirrors the
// classic onboarding layout so the two flows feel consistent.

const Container = styled(Box)(() => ({
  height: "100vh",
  width: "100vw",
  display: "flex",
  overflow: "hidden",
}));

const ContentSection = styled(Box)(({ theme }) => ({
  width: "60%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: theme.spacing(8),
  paddingLeft: theme.spacing(12),
  backgroundColor: theme.palette.background.paper,
  overflowY: "auto",
  overflowX: "hidden",
  "&::-webkit-scrollbar": { display: "none" },
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    padding: theme.spacing(4),
    paddingLeft: theme.spacing(4),
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  width: "40%",
  height: "100%",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  // Padding around the card so it isn't flush to the column edges.
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down("md")]: { display: "none" },
}));

interface GuidedLayoutProps {
  heroTitle: string;
  heroSubtitle: string;
  maxWidth?: number;
  children: React.ReactNode;
}

export default function GuidedLayout({
  heroTitle,
  heroSubtitle,
  maxWidth = 560,
  children,
}: GuidedLayoutProps) {
  const theme = useTheme();
  const { whitelabelLogos } = useWhitelabel();

  return (
    <Container>
      <ContentSection>
        <Box sx={{ width: "100%", maxWidth, py: 4 }}>{children}</Box>
      </ContentSection>

      <ImageSection>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundImage: whitelabelLogos?.onboarding_hero
              ? `url(${whitelabelLogos.onboarding_hero.url})`
              : `url(${require("../../../img/hero_tall.jpeg")})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            position: "relative",
            // Mostly-square card with softly rounded corners (not a full oval). Clip the
            // overlay to the corners.
            borderRadius: theme.spacing(4),
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.5)"
                  : "rgba(0,0,0,0.3)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              textAlign: "center",
              px: 2,
            }}
          >
            {/* Subtle dark, softly rounded panel hugging the hero copy so the
                text reads cleanly over any background image — no glass/blur. */}
            <Box
              sx={{
                borderRadius: theme.spacing(2),
                px: { xs: 3, md: 4 },
                py: { xs: 3, md: 4 },
                backgroundColor: "rgba(0,0,0,0.35)",
                maxWidth: 600,
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: { xs: "1.75rem", md: "2.5rem", lg: "3rem" },
                }}
              >
                {heroTitle}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  opacity: 0.9,
                  fontSize: { xs: "0.875rem", md: "1.125rem", lg: "1.25rem" },
                }}
              >
                {heroSubtitle}
              </Typography>
            </Box>
          </Box>
        </Box>
      </ImageSection>
    </Container>
  );
}
